import React, { useRef, useEffect, useCallback, useState } from 'react'
import {
  Box, IconButton, Tooltip, Divider, ToggleButtonGroup, ToggleButton, TextField,
} from '@mui/material'
import {
  Bold, Italic, Underline, Undo2, Redo2, Code, Eye,
  AlignLeft, AlignCenter, AlignRight, List, ListOrdered,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

const ToolbarButton = ({ icon: Icon, title, onClick, disabled }) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          borderRadius: 1, width: 30, height: 30,
          color: 'text.secondary',
          '&:hover': { backgroundColor: 'rgba(201, 169, 110, 0.08)', color: '#c9a96e' },
          '&.Mui-disabled': { opacity: 0.3 },
        }}
      >
        <Icon size={15} />
      </IconButton>
    </span>
  </Tooltip>
)

// Extract <style> blocks and dir from HTML, return { styles, body, dir }
function extractStyles(html) {
  if (!html) return { styles: '', body: '', dir: '' }
  const styleBlocks = []
  // Detect dir from <html dir="...">
  const dirMatch = html.match(/<html[^>]*\sdir=["'](\w+)["']/i)
  const dir = dirMatch ? dirMatch[1] : ''
  const body = html
    .replace(/<style[\s\S]*?<\/style>/gi, (match) => { styleBlocks.push(match); return '' })
    .replace(/<head[\s\S]*?<\/head>/gi, '')
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '')
  return { styles: styleBlocks.join('\n'), body, dir }
}

export default function RichHtmlEditor({ value, onChange, maxHeight = '65vh' }) {
  const { t } = useTranslation()
  const editorRef = useRef(null)
  const [mode, setMode] = useState('visual')
  const [rawHtml, setRawHtml] = useState(value || '')
  const lastEmittedRef = useRef(value || '')
  const initializedRef = useRef(false)
  // Preserve the original <style> block(s) so they survive visual editing
  const stylesRef = useRef('')
  // Detect and preserve document direction (rtl/ltr)
  const [docDir, setDocDir] = useState('')

  // Set initial content ONCE, then never touch innerHTML again
  useEffect(() => {
    const el = editorRef.current
    if (!el || mode !== 'visual') return
    if (!initializedRef.current) {
      const { styles, body, dir } = extractStyles(value)
      stylesRef.current = styles
      if (dir) setDocDir(dir)
      el.innerHTML = body
      lastEmittedRef.current = value || ''
      initializedRef.current = true
    }
  }, [mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // If value changes externally (not from our own onChange), update content
  useEffect(() => {
    const el = editorRef.current
    if (!el || mode !== 'visual' || !initializedRef.current) return
    // Only update if value differs from what we last emitted (external change)
    if (value !== lastEmittedRef.current) {
      const { styles, body, dir } = extractStyles(value)
      stylesRef.current = styles
      if (dir) setDocDir(dir)
      el.innerHTML = body
      lastEmittedRef.current = value || ''
    }
  }, [value, mode])

  // Sync raw html when switching to code — show full HTML with styles
  useEffect(() => {
    if (mode === 'code') {
      setRawHtml(lastEmittedRef.current)
    }
  }, [mode])

  // Emit body HTML with preserved <style> block prepended
  const emitWithStyles = useCallback((bodyHtml) => {
    const full = stylesRef.current ? stylesRef.current + '\n' + bodyHtml : bodyHtml
    lastEmittedRef.current = full
    onChange?.(full)
  }, [onChange])

  const handleInput = useCallback(() => {
    const el = editorRef.current
    if (!el) return
    emitWithStyles(el.innerHTML)
  }, [emitWithStyles])

  // Paste as plain text to preserve structure
  const handlePaste = useCallback((e) => {
    e.preventDefault()
    const text = e.clipboardData?.getData('text/plain') || ''
    document.execCommand('insertText', false, text)
  }, [])

  const execCommand = useCallback((command, val = null) => {
    if (mode !== 'visual') return
    const el = editorRef.current
    if (!el) return
    el.focus()
    document.execCommand(command, false, val)
    emitWithStyles(el.innerHTML)
  }, [mode, emitWithStyles])

  const handleModeChange = (newMode) => {
    if (!newMode || newMode === mode) return
    if (mode === 'code' && newMode === 'visual') {
      // User edited raw HTML in code mode — re-extract styles
      const { styles, body } = extractStyles(rawHtml)
      stylesRef.current = styles
      lastEmittedRef.current = rawHtml
      onChange?.(rawHtml)
      setMode(newMode)
      requestAnimationFrame(() => {
        const el = editorRef.current
        if (el) el.innerHTML = body
      })
    } else {
      setMode(newMode)
    }
  }

  const isCode = mode === 'code'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.25,
          px: 1, py: 0.5,
          borderRadius: '8px 8px 0 0',
          border: '1px solid', borderColor: 'divider', borderBottom: 'none',
          backgroundColor: 'background.paper', flexWrap: 'wrap',
        }}
      >
        <ToolbarButton icon={Bold} title="Bold (Ctrl+B)" onClick={() => execCommand('bold')} disabled={isCode} />
        <ToolbarButton icon={Italic} title="Italic (Ctrl+I)" onClick={() => execCommand('italic')} disabled={isCode} />
        <ToolbarButton icon={Underline} title="Underline (Ctrl+U)" onClick={() => execCommand('underline')} disabled={isCode} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <ToolbarButton icon={AlignLeft} title="Align left" onClick={() => execCommand('justifyLeft')} disabled={isCode} />
        <ToolbarButton icon={AlignCenter} title="Align center" onClick={() => execCommand('justifyCenter')} disabled={isCode} />
        <ToolbarButton icon={AlignRight} title="Align right" onClick={() => execCommand('justifyRight')} disabled={isCode} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <ToolbarButton icon={List} title="Bullet list" onClick={() => execCommand('insertUnorderedList')} disabled={isCode} />
        <ToolbarButton icon={ListOrdered} title="Numbered list" onClick={() => execCommand('insertOrderedList')} disabled={isCode} />
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
        <ToolbarButton icon={Undo2} title="Undo (Ctrl+Z)" onClick={() => execCommand('undo')} disabled={isCode} />
        <ToolbarButton icon={Redo2} title="Redo (Ctrl+Y)" onClick={() => execCommand('redo')} disabled={isCode} />
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          value={mode} exclusive
          onChange={(_, val) => handleModeChange(val)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              px: 1, py: 0.25, fontSize: 12, textTransform: 'none',
              border: '1px solid', borderColor: 'divider',
            },
            '& .Mui-selected': {
              backgroundColor: 'rgba(201, 169, 110, 0.12) !important',
              color: '#c9a96e !important',
            },
          }}
        >
          <ToggleButton value="visual"><Eye size={14} style={{ marginRight: 4 }} /> {t('du_visual')}</ToggleButton>
          <ToggleButton value="code"><Code size={14} style={{ marginRight: 4 }} /> {t('du_code')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Editor */}
      <Box sx={{ display: mode === 'visual' ? 'block' : 'none' }}>
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onPaste={handlePaste}
          spellCheck={false}
          dir={docDir || undefined}
          sx={{
            border: '1px solid', borderColor: 'divider',
            borderRadius: '0 0 8px 8px',
            overflow: 'auto',
            maxHeight, minHeight: 400,
            p: '24px 32px',
            backgroundColor: '#fff',
            cursor: 'text', outline: 'none',
            fontFamily: '"Arial", sans-serif',
            fontSize: 14, lineHeight: 1.8, color: '#333',
            ...(docDir === 'rtl' && { direction: 'rtl', textAlign: 'right' }),
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
            '& *': { maxWidth: '100%', boxSizing: 'border-box' },
            '& img': { maxWidth: '100%', height: 'auto' },
            '& pre': { whiteSpace: 'pre-wrap', overflowX: 'auto' },

            '& .document-container': { background: 'white', padding: '10px', margin: '0 auto', borderRadius: '8px' },
            '& .document-title': {
              color: '#BE2165', fontWeight: 'bold', fontSize: '1.2em', textAlign: 'center',
              margin: '20px 0', padding: '15px', border: '2px solid #BE2165', borderRadius: '5px',
            },
            '& .header': {
              textAlign: 'center', marginBottom: '30px', padding: '20px',
              backgroundColor: '#f8f9fa', borderBottom: '3px solid #BE2165',
            },
            '& .separator': { borderTop: 'solid #BE2165 2pt', height: '2px', margin: '20px 0' },
            '& .authority': { fontWeight: 'bold', margin: '15px 0', padding: '10px 0', borderBottom: '1px dotted #ccc' },
            '& .legal-basis': {
              margin: '10px 0', padding: '10px', backgroundColor: '#f9f9f9',
              borderRight: '4px solid #BE2165', lineHeight: 1.6,
            },
            '& .legal-basis-ltr': {
              margin: '10px 0', padding: '10px', backgroundColor: '#f9f9f9',
              borderRight: 'none', borderLeft: '4px solid #BE2165', lineHeight: 1.6,
            },
            '& .decision-header': {
              textAlign: 'center', fontWeight: 'bold', margin: '25px 0', padding: '15px',
              backgroundColor: '#f0f0f0', borderRadius: '5px',
            },
            '& .part-block': { margin: '30px 0', padding: '20px 15px 10px', borderTop: '3px solid #BE2165' },
            '& .part-title': {
              color: '#BE2165', fontWeight: 'bold', fontSize: '1.15em', textAlign: 'center',
              marginBottom: '15px', padding: '10px', backgroundColor: '#f9e6ef', borderRadius: '5px',
            },
            '& .chapter-block': { margin: '25px 0', padding: '15px 10px' },
            '& .chapter-title': {
              color: '#BE2165', fontWeight: 'bold', fontSize: '1.05em', textAlign: 'center',
              marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5',
              borderRadius: '4px', borderBottom: '2px solid #BE2165',
            },
            '& .section-block': { margin: '20px 0', padding: '10px 5px' },
            '& .section-title': { color: '#BE2165', fontWeight: 'bold', textAlign: 'center', marginBottom: '10px', padding: '6px' },
            '& .article': {
              margin: '20px 0', padding: '15px', border: '1px solid #e0e0e0',
              borderRadius: '5px', backgroundColor: '#fafafa', position: 'relative',
            },
            '& .article-title': {
              fontWeight: 'bold', color: '#BE2165', marginBottom: '10px', padding: '8px 12px',
              backgroundColor: '#f5f5f5', borderRadius: '3px', borderRight: '4px solid #BE2165',
            },
            '& .article-title-ltr': {
              fontWeight: 'bold', color: '#BE2165', marginBottom: '10px', padding: '8px 12px',
              backgroundColor: '#f5f5f5', borderRadius: '3px', borderRight: 'none', borderLeft: '4px solid #BE2165',
            },
            '& .article-content': { margin: '10px 0', lineHeight: 1.7, padding: '5px 0' },
            '& .article-paragraph': { margin: '8px 0', padding: '5px 0' },
            '& .definition-list': { margin: '15px 0', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '5px' },
            '& .definition-item': { margin: '8px 0', padding: '5px 0', borderBottom: '1px dotted #ddd' },
            '& .annexe-block': {
              border: '1px solid #e0e0e0', borderRadius: '8px', backgroundColor: '#f9e6ef',
              margin: '35px 0 0', padding: '22px 20px',
            },
            '& .annexe-title': { color: '#BE2165', fontWeight: 'bold', fontSize: '1.1em', textAlign: 'center', marginBottom: '16px' },
            '& .signature-section': {
              marginTop: '40px', padding: '20px', backgroundColor: '#f8f9fa', borderTop: '2px solid #BE2165', textAlign: 'left',
            },
            '& .publication-info': {
              marginTop: '30px', padding: '15px', backgroundColor: '#e9ecef', borderRadius: '5px', fontStyle: 'italic',
            },
            '& .legal-mention': { color: '#BE2165', backgroundColor: '#f9e6ef', display: 'block', fontSize: '0.95em' },
            '& .center': { textAlign: 'center' },
            '& table.legal-table, & table': {
              width: '100%', borderCollapse: 'collapse', margin: '20px 0', background: '#fff',
              '& th, & td': { border: '1px solid #BE2165', padding: '8px 12px', textAlign: 'left' },
              '& th': { backgroundColor: '#f8f9fa', color: '#BE2165', fontWeight: 'bold' },
            },
            '& a': { color: '#BE2165', textDecoration: 'underline' },
            '& ul, & ol': { paddingLeft: '24px', margin: '8px 0' },
            '& li': { margin: '2px 0' },
          }}
        />
      </Box>

      {mode === 'code' && (
        <TextField
          multiline value={rawHtml}
          onChange={(e) => setRawHtml(e.target.value)}
          onBlur={() => { lastEmittedRef.current = rawHtml; onChange?.(rawHtml) }}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              fontFamily: '"Fira Code", "Consolas", monospace',
              fontSize: 12, lineHeight: 1.5, borderRadius: '0 0 8px 8px',
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            '& textarea': { maxHeight, overflow: 'auto !important' },
          }}
          fullWidth
        />
      )}
    </Box>
  )
}
